import React, { useCallback, useMemo, useState } from "react";
import {
  Page,
  Layout,
  Card,
  AccountConnection,
  Avatar,
  Banner,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Link,
  Modal,
  List,
} from "@shopify/polaris";
import { useMutation } from "@apollo/client";
import { useAppBridge } from "../../Components/Shopify/ShopifyAppBridgeProvider";
import { DELETE_COMPANY_CONNECTION } from "../../graphql/mutations";
import { evictConnectionScopedData } from "../../graphql/cacheUtils";
import ChannelHelpFooter, {
  MARKETPLACE_URL,
  PRIVACY_URL,
  TERMS_URL,
} from "../../Components/Channel/ChannelHelpFooter";

type ConnectionStatusInfo = {
  shopDomain: string;
  linked: boolean;
  linkedToCurrentCompany: boolean;
  userName?: string | null;
  userEmail?: string | null;
  companyName?: string | null;
  channelConnected?: boolean | null;
  channelHandle?: string | null;
  channelError?: string | null;
};

type EmbeddedAdminHomeProps = {
  status?: ConnectionStatusInfo | null;
  refetchStatus: () => Promise<void>;
};

const EmbeddedAdminHome: React.FC<EmbeddedAdminHomeProps> = ({
  status,
  refetchStatus,
}) => {
  const { shop } = useAppBridge();
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [disconnectError, setDisconnectError] = useState<string | null>(null);
  const [deleteCompanyConnection, { loading: disconnecting }] = useMutation(
    DELETE_COMPANY_CONNECTION,
    {
      // Connection-scoped data (orders, listings, publication summary) is
      // stale the moment the store is unlinked — drop it so nothing renders
      // from cache after a re-link, possibly to a different account.
      update: (cache) => evictConnectionScopedData(cache),
    }
  );

  const accountName = useMemo(
    () => (shop ? shop.replace(".myshopify.com", "") : ""),
    [shop]
  );

  const connected = Boolean(shop);
  const channelConnected = Boolean(status?.channelConnected);

  const openDisconnect = useCallback(() => {
    setDisconnectError(null);
    setDisconnectOpen(true);
  }, []);
  const closeDisconnect = useCallback(() => {
    if (disconnecting) return;
    setDisconnectOpen(false);
  }, [disconnecting]);
  const confirmDisconnect = useCallback(async () => {
    if (!shop) return;
    setDisconnectError(null);
    try {
      const { data } = await deleteCompanyConnection({
        variables: { shopDomain: shop },
      });
      if (data?.deleteCompanyConnection === false) {
        setDisconnectError("Could not disconnect this store. Please try again.");
        return;
      }
      // Refetch connection status — EmbeddedShell will swap this view for
      // LinkStore once `linked` flips to false, which is where the user can
      // re-connect (sign in to a different ShareStock account).
      await refetchStatus();
      setDisconnectOpen(false);
    } catch (error: any) {
      setDisconnectError(
        error?.graphQLErrors?.[0]?.message ||
          error?.message ||
          "Could not disconnect this store. Please try again."
      );
    }
  }, [shop, deleteCompanyConnection, refetchStatus]);

  const terms = (
    <Text as="span" variant="bodySm">
      By connecting, you agree to the{" "}
      <Link url={TERMS_URL} target="_blank">
        ShareStock terms of service
      </Link>{" "}
      and{" "}
      <Link url={PRIVACY_URL} target="_blank">
        privacy policy
      </Link>
      .
    </Text>
  );

  return (
    <Page title="ShareStock" subtitle="Sales channel for the ShareStock marketplace">
      <Layout>
        {/* Approval / connection status (App Store 5.7.8, 5.7.15) */}
        <Layout.Section>
          {status?.channelError ? (
            <Banner title="Sales channel setup needs attention" tone="critical">
              <Text as="p" variant="bodyMd">
                Your ShareStock account is linked, but the sales channel could not be
                connected to this store: {status.channelError}
              </Text>
              <Text as="p" variant="bodyMd">
                Reload this page to retry, or contact ShareStock support if the
                problem persists.
              </Text>
            </Banner>
          ) : channelConnected ? (
            <Banner title="ShareStock is approved and connected" tone="success">
              <Text as="p" variant="bodyMd">
                {accountName || "This store"} is connected to{" "}
                {status?.companyName || "your ShareStock account"}. Products you publish to
                the ShareStock sales channel appear on the marketplace.
              </Text>
            </Banner>
          ) : (
            <Banner title="Connecting your sales channel" tone="info">
              <Text as="p" variant="bodyMd">
                Your ShareStock account is linked and the sales channel is being set up.
                Reload this page in a moment.
              </Text>
            </Banner>
          )}
        </Layout.Section>

        <Layout.AnnotatedSection
          title="Account"
          description={
            <BlockStack gap="200">
              <Text as="p" variant="bodyMd" tone="subdued">
                ShareStock is a sales channel that publishes products from your
                Shopify store to the ShareStock marketplace, where retail buyers
                discover them. Every order is placed through your store's own
                Shopify Checkout — no separate billing or off-platform payments.
              </Text>
              <Text as="p" variant="bodyMd" fontWeight="semibold">
                To use ShareStock you'll need:
              </Text>
              <List type="bullet">
                <List.Item>An active Shopify plan (any tier)</List.Item>
                <List.Item>At least one product with available inventory</List.Item>
                <List.Item>
                  A configured payment provider on your store so buyers can
                  check out
                </List.Item>
              </List>
            </BlockStack>
          }
        >
          <BlockStack gap="400">
            {status?.userEmail && (
              <Card>
                <BlockStack gap="300">
                  <Text as="h3" variant="headingSm">
                    ShareStock account
                  </Text>
                  <InlineStack gap="300" blockAlign="center" wrap={false}>
                    <Avatar
                      name={status.userName || status.userEmail}
                      initials={(status.userName || status.userEmail || "?")
                        .trim()
                        .charAt(0)
                        .toUpperCase()}
                    />
                    <BlockStack gap="050">
                      {status.userName && (
                        <Text as="p" variant="bodyMd" fontWeight="semibold">
                          {status.userName}
                        </Text>
                      )}
                      <Text as="p" variant="bodyMd" tone="subdued">
                        {status.userEmail}
                      </Text>
                      {status.companyName && (
                        <Text as="p" variant="bodySm" tone="subdued">
                          Company: {status.companyName}
                        </Text>
                      )}
                    </BlockStack>
                  </InlineStack>
                </BlockStack>
              </Card>
            )}

            <AccountConnection
              accountName={accountName}
              connected={connected}
              title="ShareStock"
              action={
                connected
                  ? { content: "Disconnect", onAction: openDisconnect }
                  : { content: "Connect", onAction: refetchStatus }
              }
              details={
                connected
                  ? `Connected as ${accountName}`
                  : "Sign in to ShareStock to connect this store."
              }
              termsOfService={terms}
            />
          </BlockStack>

          <Modal
            open={disconnectOpen}
            onClose={closeDisconnect}
            title="Disconnect ShareStock?"
            primaryAction={{
              content: "Disconnect",
              destructive: true,
              onAction: confirmDisconnect,
              loading: disconnecting,
            }}
            secondaryActions={[
              {
                content: "Cancel",
                onAction: closeDisconnect,
                disabled: disconnecting,
              },
            ]}
          >
            <Modal.Section>
              <BlockStack gap="300">
                {disconnectError && (
                  <Banner tone="critical">{disconnectError}</Banner>
                )}
                <Text as="p" variant="bodyMd">
                  This unlinks {accountName || "your store"} from your
                  ShareStock account and removes the ShareStock sales channel
                  from this store. Your products will stop appearing on the
                  marketplace and buyers will no longer be able to order against
                  your inventory.
                </Text>
                <Text as="p" variant="bodyMd">
                  The ShareStock app stays installed on your Shopify store, so
                  you can re-connect at any time by signing in again. To fully
                  uninstall, remove ShareStock from your Shopify Apps page.
                </Text>
              </BlockStack>
            </Modal.Section>
          </Modal>
        </Layout.AnnotatedSection>

        <Layout.AnnotatedSection
          title="Pricing and commission"
          description="What it costs to sell through ShareStock."
        >
          <Card>
            <BlockStack gap="200">
              <Text as="p" variant="bodyMd">
                ShareStock is <Text as="span" fontWeight="semibold">free to install</Text>.
                There are no monthly fees, no listing fees, and{" "}
                <Text as="span" fontWeight="semibold">no commission</Text> on sales made
                through the channel.
              </Text>
              <Text as="p" variant="bodyMd" tone="subdued">
                Buyers pay through standard Shopify Checkout on your store, so your
                usual Shopify transaction fees apply. You keep full control of pricing
                and fulfilment.
              </Text>
            </BlockStack>
          </Card>
        </Layout.AnnotatedSection>

        <Layout.AnnotatedSection
          title="Publishing"
          description="Choose which products are listed on the ShareStock marketplace. You stay in control from the Publishing page or from any product page in Shopify."
        >
          <Card>
            <BlockStack gap="300">
              <Text as="p" variant="bodyMd">
                Publish products to the ShareStock sales channel to list them on the
                marketplace. Unpublish a product to take it off. Prices, inventory and
                images stay in sync with Shopify.
              </Text>
              <List type="number">
                <List.Item>
                  Open <Text as="span" fontWeight="semibold">Publishing</Text> and choose{" "}
                  <Text as="span" fontWeight="semibold">Add products</Text>, or
                </List.Item>
                <List.Item>
                  On a product page in Shopify, under{" "}
                  <Text as="span" fontWeight="semibold">Sales channels and apps</Text>,
                  turn on ShareStock, or
                </List.Item>
                <List.Item>
                  Select several products in your product list and use the bulk editor.
                </List.Item>
              </List>
              <InlineStack gap="200">
                <Button url="/publishing" variant="primary" disabled={!channelConnected}>
                  Manage publishing
                </Button>
              </InlineStack>
            </BlockStack>
          </Card>
        </Layout.AnnotatedSection>

        <Layout.AnnotatedSection
          title="Where products appear"
          description="Browse the marketplace where your published products are listed."
        >
          <Card>
            <BlockStack gap="300">
              <Text as="p" variant="bodyMd">
                Published products appear at{" "}
                <Link url={MARKETPLACE_URL} target="_blank">
                  mysharestock.org/browse
                </Link>
                . Orders placed there land in your Shopify Orders list, attributed to
                the ShareStock sales channel.
              </Text>
              <InlineStack gap="200">
                <Button url={MARKETPLACE_URL} target="_blank">
                  Open ShareStock marketplace
                </Button>
                <Button url="shopify://admin/orders" target="_top">
                  Open orders
                </Button>
              </InlineStack>
            </BlockStack>
          </Card>
        </Layout.AnnotatedSection>

        <Layout.Section>
          <Banner title="Setup checklist" tone="info">
            <BlockStack gap="100">
              <Text as="p" variant="bodyMd">
                1. Confirm your account is connected above.
              </Text>
              <Text as="p" variant="bodyMd">
                2. Publish the products you want to sell from the Publishing page.
                They appear on the marketplace right away; unpublish to unlist.
              </Text>
              <Text as="p" variant="bodyMd">
                3. Orders received via ShareStock appear in your normal Shopify
                Orders list with the ShareStock sales channel.
              </Text>
            </BlockStack>
          </Banner>
        </Layout.Section>

        <Layout.Section>
          <ChannelHelpFooter />
        </Layout.Section>
      </Layout>
    </Page>
  );
};

export default EmbeddedAdminHome;
